import { forwardRef, useMemo, useState } from 'react'
import {Circle, Play, Ruler} from 'lucide-react'
import SolverCard from '@/components/SolverCard'
import CopyButton from '@/components/CopyButton'
import EmailSolutionButton from '@/components/EmailSolutionButton'

interface Props {
  highlighted: boolean
}

const CircleCalculator = forwardRef<HTMLElement, Props>(({ highlighted }, ref) => {
  const [mode, setMode] = useState<'radius' | 'diameter'>('radius')
  const [value, setValue] = useState(6)
  const [unrolling, setUnrolling] = useState(false)

  const radius = mode === 'radius' ? value : value / 2
  const diameter = mode === 'diameter' ? value : value * 2
  const circumference = 2 * Math.PI * radius
  const cFixed = circumference.toFixed(4)

  const viewBox = 320
  const cx = viewBox / 2
  const cy = viewBox / 2
  const maxR = 120
  const displayR = Math.max(8, Math.min(maxR, (radius / 30) * maxR))
  const circumferencePx = 2 * Math.PI * displayR

  const triggerUnroll = () => {
    setUnrolling(false)
    requestAnimationFrame(() => setUnrolling(true))
    setTimeout(() => setUnrolling(false), 2400)
  }

  const md = useMemo(() => {
    return `# Circle Circumference\n\n- Mode: ${mode === 'radius' ? `Radius r = ${radius}` : `Diameter d = ${diameter}`}\n- Formula: C = 2 * π * r = π * d\n- Computation: C = 2 × π × ${radius} = ${cFixed}\n- Result: **C ≈ ${cFixed}**`
  }, [mode, radius, diameter, cFixed])

  return (
    <SolverCard
      ref={ref}
      id="circle"
      eyebrow="Geometry · 33,100/mo"
      title="Circle Circumference Formula Calculator (Step-by-Step)"
      volume="Vol 33.1K"
      highlighted={highlighted}
    >
      <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-6">
        The <strong className="text-slate-200">circumference</strong> of a circle is the linear distance around its boundary.
        Using the radius <span className="font-mono-math text-emerald-300">r</span>, the formula is{' '}
        <span className="font-mono-math text-emerald-300">C = 2 · π · r</span>. With the diameter{' '}
        <span className="font-mono-math text-emerald-300">d = 2r</span>, the equivalent form is{' '}
        <span className="font-mono-math text-emerald-300">C = π · d</span>. Drag the slider below — the SVG circle scales live, and the
        unroll animation flattens the boundary onto a green ruler ribbon to show what the formula actually measures.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Controls */}
        <div className="space-y-5">
          <div className="flex gap-2">
            {(['radius', 'diameter'] as const).map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                  mode === m
                    ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200'
                    : 'border-[var(--line)] bg-white/[0.02] text-slate-300 hover:border-[var(--line-strong)]'
                }`}
              >
                Use {m === 'radius' ? 'Radius (r)' : 'Diameter (d)'}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <label className="text-xs uppercase tracking-wider text-slate-400">
                {mode === 'radius' ? 'Radius r' : 'Diameter d'}
              </label>
              <span className="font-mono-math text-emerald-300 text-lg">{value.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              step={0.1}
              value={value}
              onChange={e => setValue(Number.parseFloat(e.target.value))}
              className="range-pro"
            />
          </div>

          <button
            onClick={triggerUnroll}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-400 text-[#06281b] font-semibold hover:bg-emerald-300 transition shadow-lg shadow-emerald-500/20"
          >
            <Play className="w-4 h-4" />
            Unroll Boundary onto Ruler
          </button>

          {/* Step-by-step */}
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-slate-400">Step-by-Step Solver</div>
            <div className="grid gap-2">
              <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
                <span className="text-slate-500">1.</span> C = 2 · π · r
              </div>
              <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
                <span className="text-slate-500">2.</span> C = 2 · π · {radius.toFixed(2)}
              </div>
              <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
                <span className="text-slate-500">3.</span> C = {(2 * radius).toFixed(2)} · π
              </div>
              <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] px-3.5 py-2.5 font-mono-math text-sm text-emerald-200">
                <span className="text-emerald-400/80">=</span> C ≈ {cFixed}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-2">
              <CopyButton markdown={md} />
              <EmailSolutionButton title="Circle Circumference Formula Calculator" markdown={md} />
            </div>
          </div>
        </div>

        {/* Visual */}
        <div className="rounded-xl border border-[var(--line)] bg-[#0a0e1a] p-4 blueprint-grid relative overflow-hidden">
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            <Circle className="w-3 h-3" /> Live Model
          </div>
          <svg viewBox={`0 0 ${viewBox} ${viewBox}`} className="w-full h-auto">
            <defs>
              <radialGradient id="circ-fill" cx="50%" cy="45%" r="60%">
                <stop offset="0%" stopColor="#6ee7ff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#38e1a4" stopOpacity="0.04" />
              </radialGradient>
            </defs>

            {/* Static circle */}
            {!unrolling && (
              <>
                <circle cx={cx} cy={cy} r={displayR} fill="url(#circ-fill)" stroke="#38e1a4" strokeWidth={2} />
                {/* radius / diameter line */}
                {mode === 'radius' ? (
                  <>
                    <line x1={cx} y1={cy} x2={cx + displayR} y2={cy} stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="4 3" />
                    <text x={cx + displayR / 2} y={cy - 6} fill="#fbbf24" fontSize="11" fontFamily="JetBrains Mono" textAnchor="middle">r = {radius.toFixed(2)}</text>
                  </>
                ) : (
                  <>
                    <line x1={cx - displayR} y1={cy} x2={cx + displayR} y2={cy} stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="4 3" />
                    <text x={cx} y={cy - 6} fill="#fbbf24" fontSize="11" fontFamily="JetBrains Mono" textAnchor="middle">d = {diameter.toFixed(2)}</text>
                  </>
                )}
                <circle cx={cx} cy={cy} r={2.5} fill="#fbbf24" />
              </>
            )}

            {/* Unroll animation */}
            {unrolling && (
              <g>
                <circle
                  cx={cx}
                  cy={cy}
                  r={displayR}
                  fill="none"
                  stroke="#38e1a4"
                  strokeWidth={2.5}
                  strokeDasharray={circumferencePx}
                  strokeDashoffset={circumferencePx}
                  style={{ animation: 'unrollDash 1.6s ease-out forwards' }}
                />
                <line
                  x1={20}
                  y1={viewBox - 30}
                  x2={20}
                  y2={viewBox - 30}
                  stroke="#38e1a4"
                  strokeWidth={4}
                  strokeLinecap="round"
                  style={{ animation: `unrollLine ${1.6}s ease-out 0.2s forwards` }}
                />
                {/* tick marks */}
                {Array.from({ length: 11 }).map((_, i) => {
                  const x = 20 + ((viewBox - 40) / 10) * i
                  return <line key={i} x1={x} y1={viewBox - 36} x2={x} y2={viewBox - 24} stroke="#38e1a4" strokeWidth={1} opacity={0.6} style={{ animation: `tickFade 0.3s ease ${0.6 + i * 0.08}s both` }} />
                })}
                <text x={viewBox / 2} y={viewBox - 8} fill="#38e1a4" fontSize="11" fontFamily="JetBrains Mono" textAnchor="middle">
                  C ≈ {cFixed}
                </text>
              </g>
            )}
          </svg>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Boundary length</span>
            <span className="font-mono-math text-emerald-300">{cFixed}</span>
          </div>
          <style>{`
            @keyframes unrollDash {
              to { stroke-dashoffset: 0; opacity: 0.25; }
            }
            @keyframes unrollLine {
              to { x2: ${viewBox - 20}; }
            }
            @keyframes tickFade {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 0.6; transform: translateY(0); }
            }
          `}</style>
        </div>
      </div>
    </SolverCard>
  )
})

CircleCalculator.displayName = 'CircleCalculator'
export default CircleCalculator
